/**
 * Branding Plugin
 * Loads and provides centralized branding configuration for multi-instance support
 */

import { brandLogo } from '@/utils/brandAssets'

let brandingConfig = null
let brandingLoaded = false
let loadingPromise = null

// Default fallback (only used if config fails to load)
const defaultBranding = {
  company: {
    name: 'Delivery App',
    shortName: 'App',
    tagline: 'Service de livraison',
    slogan: '',
    email: '',
    signatureLabel: 'Cachet et signature',
  },
  theme: {
    primary: '#E40D14',
    primaryDark: '#BD060B',
    primaryLight: '#F04D52',
    secondary: '#041524',
    secondaryLight: '#0A2A48',
    accent: '#E40D14',
    accentAlt: '#F04D52',
    accentDark: '#BD060B',
  },
  menu: {
    background: '#ffffff',
    borderColor: '#f0f0f0',
    textColor: '#1D1D1B',
    hoverBackground: '#ffe8e8',
    activeBackground: '#ffe8e8',
    activeBorderColor: '#ff6b66',
    toggleHoverColor: '#ff6b66',
    toggleHoverAltColor: '#EB2322',
    sectionHeaderColor: '#999999',
    scrollbarTrack: '#f8f8f8',
    scrollbarThumb: '#dddddd',
    scrollbarThumbHover: '#cccccc',
  },
  logo: {
    ...brandLogo,
  },
  meta: {
    title: 'Delivery Platform',
    description: 'Delivery management system',
  },
  features: {
    showSlogan: false,
  },
}

function hexToRgbValue(hex) {
  if (typeof hex !== 'string' || !/^#[0-9a-fA-F]{6}$/.test(hex)) return null
  const value = hex.replace('#', '')
  return `${parseInt(value.slice(0, 2), 16)}, ${parseInt(value.slice(2, 4), 16)}, ${parseInt(value.slice(4, 6), 16)}`
}

/**
 * Load branding configuration from JSON file
 */
async function loadBranding() {
  if (brandingLoaded) {
    return brandingConfig
  }

  if (loadingPromise) {
    return loadingPromise
  }

  loadingPromise = (async () => {
    try {
      const response = await fetch(`${process.env.BASE_URL || '/'}config/branding.json`)
      if (!response.ok) {
        throw new Error(`Failed to load branding config: ${response.status}`)
      }
      const config = await response.json()
      brandingConfig = {
        ...defaultBranding,
        ...config,
        menu: { ...defaultBranding.menu, ...(config.menu || {}) },
        logo: { ...brandLogo, alt: config.logo?.alt || brandLogo.alt },
      }
      brandingLoaded = true
      return brandingConfig
    } catch (error) {
      brandingConfig = defaultBranding
      brandingLoaded = true
      return brandingConfig
    }
  })()

  return loadingPromise
}

/**
 * Apply CSS variables to document root
 */
function applyCSSVariables(config) {
  const root = document.documentElement
  const { theme, menu } = config

  // Apply theme colors as CSS variables
  root.style.setProperty('--brand-primary', theme.primary)
  root.style.setProperty('--brand-primary-dark', theme.primaryDark)
  root.style.setProperty('--brand-primary-light', theme.primaryLight)
  root.style.setProperty('--brand-secondary', theme.secondary)
  root.style.setProperty('--brand-secondary-light', theme.secondaryLight)
  root.style.setProperty('--brand-primary-rgb', hexToRgbValue(theme.primary) || '228, 13, 20')
  root.style.setProperty('--brand-primary-dark-rgb', hexToRgbValue(theme.primaryDark) || '189, 6, 11')
  root.style.setProperty('--brand-primary-light-rgb', hexToRgbValue(theme.primaryLight) || '240, 77, 82')
  root.style.setProperty('--brand-secondary-rgb', hexToRgbValue(theme.secondary) || '4, 21, 36')
  root.style.setProperty('--brand-secondary-light-rgb', hexToRgbValue(theme.secondaryLight) || '10, 42, 72')
  root.style.setProperty('--brand-red', theme.primary)
  root.style.setProperty('--brand-red-dark', theme.primaryDark)
  root.style.setProperty('--brand-red-light', theme.primaryLight)
  root.style.setProperty('--brand-black', theme.secondary)
  root.style.setProperty('--brand-black-light', theme.secondaryLight)
  root.style.setProperty('--brand-accent', theme.accent)
  root.style.setProperty('--brand-accent-alt', theme.accentAlt)
  root.style.setProperty('--brand-accent-dark', theme.accentDark)
  root.style.setProperty('--brand-gradient', `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`)

  // Apply menu colors as CSS variables
  if (menu) {
    root.style.setProperty('--menu-bg', menu.background)
    root.style.setProperty('--menu-border-color', menu.borderColor)
    root.style.setProperty('--menu-text-color', menu.textColor)
    root.style.setProperty('--menu-hover-bg', menu.hoverBackground)
    root.style.setProperty('--menu-active-bg', menu.activeBackground)
    root.style.setProperty('--menu-active-border-color', menu.activeBorderColor)
    root.style.setProperty('--menu-toggle-hover-color', menu.toggleHoverColor)
    root.style.setProperty('--menu-toggle-hover-alt-color', menu.toggleHoverAltColor)
    root.style.setProperty('--menu-section-header-color', menu.sectionHeaderColor)
    root.style.setProperty('--menu-scrollbar-track', menu.scrollbarTrack)
    root.style.setProperty('--menu-scrollbar-thumb', menu.scrollbarThumb)
    root.style.setProperty('--menu-scrollbar-thumb-hover', menu.scrollbarThumbHover)
  }
}

/**
 * Update document meta tags
 */
function updateMetaTags(config) {
  const { meta, company } = config

  // Update page title
  if (meta.title) {
    document.title = meta.title
  }

  // Update meta description
  const descriptionMeta = document.querySelector('meta[name="description"]')
  if (descriptionMeta && meta.description) {
    descriptionMeta.setAttribute('content', meta.description)
  }

  // Update Open Graph tags if they exist
  const ogTitle = document.querySelector('meta[property="og:title"]')
  if (ogTitle) {
    ogTitle.setAttribute('content', meta.title || company.name)
  }

  const ogDescription = document.querySelector('meta[property="og:description"]')
  if (ogDescription) {
    ogDescription.setAttribute('content', meta.description || company.tagline)
  }
}

/**
 * Get branding config synchronously (returns cached or null)
 */
function getBranding() {
  return brandingConfig || defaultBranding
}

/**
 * Vue Plugin Installation
 */
const BrandingPlugin = {
  /* eslint-disable no-param-reassign */
  install(Vue) {
    // Add global $branding property
    Vue.prototype.$branding = getBranding()

    // Add global mixin to ensure branding is available
    Vue.mixin({
      beforeCreate() {
        // Update $branding reference in case it was loaded after initial access
        if (brandingConfig) {
          this.$branding = brandingConfig
        }
      },
    })

    // Add global method to reload branding
    Vue.prototype.$reloadBranding = async function reloadBranding() {
      brandingLoaded = false
      loadingPromise = null
      await loadBranding()
      applyCSSVariables(brandingConfig)
      updateMetaTags(brandingConfig)
      this.$branding = brandingConfig
      return brandingConfig
    }
  },
  /* eslint-enable no-param-reassign */
}

/**
 * Initialize branding (call before Vue app creation)
 */
async function initBranding() {
  const config = await loadBranding()
  applyCSSVariables(config)
  updateMetaTags(config)
  return config
}

export {
  BrandingPlugin,
  initBranding,
  loadBranding,
  getBranding,
  defaultBranding,
}

export default BrandingPlugin
